import { useEffect, useRef } from 'react';
import { pageAuthorities } from '@/shared/auth/has-any-authority';
import { useHasAnyAuthority } from '@/shared/auth/use-has-any-authority';
import { APP_PERMISSIONS } from '@/shared/constants/app-permissions';
import { agentPanelActions } from '@/shared/reducers/casePanel.reducer';
import { extractPhoneDigits } from '@/shared/services/agentPanelPhone.mapper';
import { useSipCall } from '@/shared/providers/SipCallProvider';
import { useAppDispatch } from '@/store';
/**
 * On inbound SIP ring: populate agent-panel Redux with caller context and run lookup —
 * without navigating away from the agent's current page.
 *
 * Customer 360 registration UI lives only on {@link AgentPanelPage} (`agent-panel.tsx`).
 */
export function AgentInboundCallBridge() {
    const dispatch = useAppDispatch();
    const { incomingRing, isOnBreak } = useSipCall();
    const canUseAgentPanel = useHasAnyAuthority(pageAuthorities(APP_PERMISSIONS.CASEMANAGEMENT_AGENTPANEL));
    const processedDigitsRef = useRef(null);
    useEffect(() => {
        if (!canUseAgentPanel || isOnBreak) {
            processedDigitsRef.current = null;
            return;
        }
        const callerLabel = incomingRing?.callerLabel;
        if (!callerLabel) {
            processedDigitsRef.current = null;
            return;
        }
        const digits = extractPhoneDigits(callerLabel);
        if (!digits || processedDigitsRef.current === digits) {
            return;
        }
        processedDigitsRef.current = digits;
        dispatch(agentPanelActions.clearAgentCoreBanking());
        dispatch(agentPanelActions.setInboundCallingNumber(digits));
        dispatch(agentPanelActions.setAgentPanelPhone({ number: digits }));
        void dispatch(agentPanelActions.phoneLookup(digits));
        void dispatch(agentPanelActions.getIvrTracksBySrc(digits));
        void dispatch(agentPanelActions.fetchInboundQueueLog(digits));
    }, [canUseAgentPanel, dispatch, incomingRing?.callerLabel, isOnBreak]);
    return null;
}
